import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RotateCcw, UserX } from 'lucide-react'
import { api } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'

/**
 * Admin-only: create accounts and assign the two roles.
 *
 *   Viewer — reads everything, including revealing and copying logins,
 *            but cannot change anything
 *   Editor — all of that plus full editing: pages, logins, clients, categories
 *
 * No invite emails (no mail service on the NAS): the admin sets an initial
 * password and hands it over; people change it themselves under Account.
 */
export default function TeamManager() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data } = useQuery({ queryKey: ['users'], queryFn: () => api('/users') })
  const [adding, setAdding] = useState(false)

  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] })
  const myId = me.data?.user?.id

  async function setRole(u, role) {
    try {
      await api(`/users/${u.id}`, { method: 'PATCH', body: { role } })
      refresh()
      toast(`${u.name} is now ${role === 'editor' ? 'an editor' : 'a viewer'}`)
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
    }
  }

  async function toggleActive(u) {
    const deactivating = u.isActive
    if (deactivating && !window.confirm(`Deactivate ${u.name}? They're signed out everywhere immediately.`)) return
    try {
      await api(`/users/${u.id}`, { method: 'PATCH', body: { isActive: !u.isActive } })
      refresh()
      toast(deactivating ? `${u.name} deactivated` : `${u.name} reactivated`)
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
    }
  }

  async function resetPassword(u) {
    const next = generatePassword()
    if (!window.confirm(`Reset ${u.name}'s password? They're signed out everywhere and you'll hand them the new one.`)) return
    try {
      await api(`/users/${u.id}`, { method: 'PATCH', body: { password: next } })
      window.prompt(`New password for ${u.name} — copy it now, it won't be shown again:`, next)
      toast('Password reset')
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
    }
  }

  return (
    <section>
      <h2 className="section-header mb-2">Team</h2>
      <div className="card divide-y divide-hairline">
        {(data?.users || []).map((u) => (
          <div key={u.id} className={'flex items-center gap-3 px-4 py-3' + (u.isActive ? '' : ' opacity-50')}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-ink">
                {u.name}
                {u.id === myId && <span className="ml-1.5 text-xs font-normal text-faint">(you)</span>}
                {!u.isActive && <span className="ml-1.5 text-xs font-normal text-danger">deactivated</span>}
              </div>
              <div className="truncate text-xs text-muted">{u.email}</div>
            </div>

            {u.role === 'admin' ? (
              <span className="rounded-md bg-ink px-2 py-1 text-[11px] font-medium text-white">Admin</span>
            ) : (
              <>
                <RoleToggle user={u} onChange={setRole} />
                <button onClick={() => resetPassword(u)} title="Reset password" className="tap flex items-center justify-center rounded-lg text-faint active:bg-grouped active:text-ink">
                  <RotateCcw size={14} />
                </button>
                <button
                  onClick={() => toggleActive(u)}
                  title={u.isActive ? 'Deactivate' : 'Reactivate'}
                  className="tap flex items-center justify-center rounded-lg text-faint active:bg-grouped active:text-danger"
                >
                  <UserX size={14} />
                </button>
              </>
            )}
          </div>
        ))}

        {adding ? (
          <NewUserRow
            onDone={(created) => {
              setAdding(false)
              if (created) refresh()
            }}
          />
        ) : (
          <button onClick={() => setAdding(true)} className="tap flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-link active:bg-grouped">
            <Plus size={15} /> Add team member
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-faint">
        Viewers can read everything and copy passwords, but can't change anything. Editors can also make changes. Role changes apply on their very next tap.
      </p>
    </section>
  )
}

function RoleToggle({ user, onChange }) {
  return (
    <div className="flex shrink-0 overflow-hidden rounded-lg border border-hairline text-[11px] font-medium">
      {['viewer', 'editor'].map((r) => (
        <button
          key={r}
          onClick={() => user.role !== r && onChange(user, r)}
          className={'px-2.5 py-1.5 capitalize ' + (user.role === r ? 'bg-navy text-white' : 'bg-white text-muted active:bg-grouped')}
        >
          {r}
        </button>
      ))}
    </div>
  )
}

function NewUserRow({ onDone }) {
  // Default is an INVITE: no password — they create their own at first
  // sign-in (email + blank password on the login screen). Typing one here
  // still works for the hand-it-over-in-person path.
  const [form, setForm] = useState({ name: '', email: '', role: 'editor', password: '' })
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  async function create() {
    setBusy(true)
    try {
      const body = { name: form.name.trim(), email: form.email.trim(), role: form.role }
      if (form.password) body.password = form.password
      await api('/users', { body })
      if (form.password) {
        window.prompt(`${form.name}'s initial password — copy it now and hand it over:`, form.password)
        toast('Account created')
      } else {
        toast(`${form.name.trim()} can now sign in — email + blank password to set their own`)
      }
      onDone(true)
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
      setBusy(false)
    }
  }

  const field =
    'tap w-full rounded-xl border border-hairline bg-white px-3 py-2 text-[16px] placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-link'

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="grid grid-cols-2 gap-2">
        <input autoFocus placeholder="Name" value={form.name} onChange={set('name')} className={field} />
        <input placeholder="Email or username" value={form.email} onChange={set('email')} autoComplete="off" className={field} />
      </div>
      <div className="flex items-center gap-2">
        <select value={form.role} onChange={set('role')} className={field + ' flex-1'}>
          <option value="editor">Editor — can change anything</option>
          <option value="viewer">Viewer — read-only (can copy passwords)</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input placeholder="Password — blank: they set their own at first sign-in" value={form.password} onChange={set('password')} autoComplete="off" className={field + ' flex-1 font-mono'} />
        <button type="button" onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))} className="tap shrink-0 rounded-xl border border-hairline px-3 text-sm text-muted active:bg-grouped">
          New
        </button>
      </div>
      <div className="flex gap-2">
        <button onClick={create} disabled={busy || !form.name.trim() || !form.email.trim() || (form.password.length > 0 && form.password.length < 12)} className="tap rounded-xl bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
          {busy ? 'Creating…' : 'Create account'}
        </button>
        <button onClick={() => onDone(false)} className="tap rounded-xl px-3 py-2 text-sm text-muted">
          Cancel
        </button>
      </div>
    </div>
  )
}

/** Readable random initial password, 3×4 chars — long enough for the 12-char
 *  floor, easy to read out loud across a control room. */
function generatePassword() {
  const abc = 'abcdefghjkmnpqrstuvwxyzACDEFHJKLMNPQRSTUVWXYZ23456789'
  const chunk = () => Array.from(crypto.getRandomValues(new Uint32Array(4)), (n) => abc[n % abc.length]).join('')
  return `${chunk()}-${chunk()}-${chunk()}`
}
